import React from "react"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import PropTypes from "prop-types"
import { t } from "i18next"
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import InputRHF from "components/RHF/InputRHF"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { updateFolder } from "api/foldersApi"
import ConditionsFieldArray from "./ConditionsFieldArray"
import LoadingButton from "components/Common/LoadingButton"
import { toast } from "react-toastify"
import { folder_schema } from "helpers/validationSchema"
import useFetchOptions from "hooks/useFetchOptions"
import SelectRHF from "components/RHF/SelectRHF"

function EditFolderModal({ isOpen, toggle, folderData, ...otherProps }) {
  const { id, group, name, template } = folderData
  const queryClient = useQueryClient()

  const methods = useForm({
    defaultValues: {
      name,
      template: String(template),
      group,
    },
    resolver: yupResolver(folder_schema),
  })

  const { handleSubmit, control, reset } = methods

  const { mutate, isPending } = useMutation({
    mutationFn: data => updateFolder(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries("folders")
      toggle()
      reset()
    },
    onError: error => toast.error(error),
  })

  const templateSelect = useFetchOptions({
    url: "/controls/template_select/",
    queryKey: "template_select",
  })

  return (
    <Modal size="lg" centered isOpen={isOpen} toggle={toggle} {...otherProps}>
      <ModalHeader toggle={toggle}>{t("edit folder")}</ModalHeader>
      <ModalBody>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(mutate)} id="form_id">
            <InputRHF
              name="name"
              label={t("name")}
              placeholder={t("enter folder name")}
              control={control}
            />
            <SelectRHF
              name="template"
              label={t("template")}
              placeholder={t("select template")}
              control={control}
              options={templateSelect.options}
              isLoading={templateSelect.isLoading}
              onMenuOpen={templateSelect.fetchOptions}
            />
            <ConditionsFieldArray />
          </form>
        </FormProvider>
      </ModalBody>
      <ModalFooter>
        <Button outline color="danger" onClick={toggle}>
          {t("cancel")}
        </Button>
        <LoadingButton
          isLoading={isPending}
          type="submit"
          color="primary"
          form="form_id"
        >
          {t("save")}
        </LoadingButton>
      </ModalFooter>
    </Modal>
  )
}
EditFolderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  folderData: PropTypes.object,
}
export default EditFolderModal
